// CORE IMPORTS
import { useState, useMemo, useEffect, useRef } from "react";
import { Typography, Box, styled } from "@mui/material";
import { debounce } from "lodash";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

// COMPONENTS
import TradeCoinSelectionButton from "../../components/TradeCoinSelectionButton";
import TradeConfirmationModal from "../../components/TradeConfirmationModal";
import TradeLockedUserModal from "./TradeLockedUserModal";
import { formatPrice } from "src/components/portfolio/helpers";
import { BalanceDisplay } from "../../components/BalanceDisplay";
import { TradeSubmitButton } from "../../components/TradeSubmit";
import { OrderSwitch } from "./OrderSwitch";
import { TradeSummary } from "../../components/TradeSummary";
import { ClearButton } from "../../components/ClearButton";
import { PromotionalFeeDisplay } from "./PromotionalFeeDisplay";

// STYLES
import { SwapStyles } from "../styles/SwapStyles";
import {
  Wrapper,
  HeaderWrapper,
  InputsWrapper,
  InputWrapper,
  CustomInputField,
  SummaryAndButtonWrapper,
  FeeText,
  MaxButton,
} from "../../styles/Trade.styles";

// REDUX
import api from "src/store/features/api";
import { SanitisedTradeSummaryRes, TradeSummaryDto } from "src/store/features/api/generated";
import {
  setKnownSide,
  transerSellMaxTotal,
  useSwapSlice,
  setTradeDetails,
  resetTradeDetails,
  resetTradeRequest,
  setTradeRequest,
  ISwapDetails,
} from "src/store/features/swap.slice";
import { useUserSlice } from "src/store/features/user.slice";

// HOOKS
import useTradeErrors from "../hooks/useTradeErrors";
import useTradeInputs from "../hooks/useTradeInputs";
import useTradeRefLogic from "../hooks/useTradeRefLogic";
import useTradeSummary from "../hooks/useTradeSummary";
import useTradeUI from "../hooks/useTradeUI";
import usePermission from "src/utils/hooks/usePermission";
import { use2FACheck } from "../../hooks/use2FACheck";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// CONSTANTS
import { HELP_TOOLTIP } from "../../constants";

// UTILS
import { NumericFormatCustom } from "../../utils";
import { AlertCard, AlertStyleType } from "src/components/alerts/AlertCard";
import { getRestrictedCountryMessage } from "src/utils/restrictedCountry";

interface SwapIconWrapperProps {
  clicked: boolean;
  disable: boolean;
}
export const SwapIconWrapper = styled("div")<SwapIconWrapperProps>(({ disable }) => ({
  position: "relative",
  // using -16px specifically to counter the 16px padding on the input field and align it to the center
  margin: "-16px 0",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "#FFFFFF",
  background: "#002035",
  borderRadius: "20px",
  width: "40px",
  height: "32px",
  cursor: disable ? "default" : "pointer",
  pointerEvents: disable ? "none" : "auto",
  "&:hover": {
    background: "#4E7086",
  },
}));

export const SwapCard = () => {
  const [sellAmountValue, setSellAmountValue] = useState<string>("0");
  const [buyAmountValue, setBuyAmountValue] = useState<string>("0");

  const classes = SwapStyles();
  const location = useLocation();
  const {
    details: { customerId },
    defaultCurrency,
    address,
  } = useUserSlice();
  const { knownSide, tradeDetails } = useSwapSlice();
  const { code: userBaseCurrency } = defaultCurrency;
  const { isGuest } = usePermission();
  const history = useHistory();
  const dispatch = useDispatch();

  // user id ref
  const currencyUserID = useRef<string>(customerId);

  // APIs
  const { data: user } = api.useGetUserDatav2Query();
  const [postTradeSummary, summaryResp] = api.useGetTradeSummaryMutation();
  const [getPartnershipInfo] = api.useLazyGetReferalPartnerDetailsQuery();

  // ipnut ref logic - hook
  const {
    ref,
    handleSellInputKeyDown,
    handleBuyInputKeyDown,
    handleInputFocus,
    handleInputBlur,
    resetInputRefs,
  } = useTradeRefLogic();

  // user input data - hook
  const { sellInput, handleSellInputChange, buyInput, handleBuyInputChange, resetInputs } =
    useTradeInputs();

  const { is2FAEnabled, handle2FACheck, TwoFAAlert } = use2FACheck({
    trackingCategory: "Swap",
  });

  // summary logic - hook
  const { handleSummaryChange } = useTradeSummary({ buyInput });

  // trade UI logic - hook
  const {
    clearButton,
    submitButton,
    isTradeConfirmationOpen,
    userLocked,
    promotionalPlacardMessage,
    resetTradeUI,
    handleSubmitButtonChange,
    handleToggleTradeConfirmation,
    handleUserLockedChange,
    handlePromotionalPlacardMessage,
  } = useTradeUI({
    summaryIsLoading: summaryResp.isLoading,
  });

  // error handling logic - hook
  const { error, hasError, changeErrorMessage, handleCustomError, handleErrorFromSummaryResponse } =
    useTradeErrors({ sellInput, buyInput, ref, handleSubmitButtonChange });

  const isSubmitButtonDisabled = isGuest
    ? false
    : (hasError && !submitButton.text.includes("Deposit")) ||
      user?.isLocked ||
      submitButton.disabled ||
      clearButton.isLoading ||
      tradeDetails.total === 0;

  const submitButtonText = isGuest
    ? "Verify Now"
    : !is2FAEnabled
    ? "Enable 2FA"
    : submitButton.text;

  /**
   * Resets all values to their default state.
   *
   * This function resets various states to their initial default states.
   */
  const resetToDefault = () => {
    resetInputs();
    dispatch(resetTradeDetails());
    dispatch(resetTradeRequest());
    resetInputRefs();
    changeErrorMessage("reset");
    resetTradeUI();
  };

  const updateTradeSummary = (summary: SanitisedTradeSummaryRes): void => {
    const approx = summary.knownSide === "sell" ? summary.buy : summary.sell;
    const feeIncrease = summary.fee?.value || 0;
    const totalValue = summary.totalValue - feeIncrease;
    const isPromotional = summary.fee?.isPromotional ?? false;
    const partnershipCode = summary.fee?.promotionalPartnerCode ?? null;

    const updatedSummary: ISwapDetails = {
      total: summary.totalValue,
      isPromotional,
      partnershipCode,
      expectedOutput: {
        header: `Approx. ${approx.units.toLocaleString()} ${approx.currencyConfig.code}`,
        fee: summary.fee?.amount ?? 0,
        value: totalValue.toFixed(2),
      },
    };

    dispatch(setTradeDetails(updatedSummary));
  };

  /**
   * useEffect to reset the state to default if the customer ID changes.
   *
   * This effect monitors the `customerId` dependency and compares it with the `currencyUserID`.
   * If the `customerId` changes and does not match `currencyUserID.current`, the state is reset to default.
   * Because we also have a feature to populate the buy field with a currency code from the URL,
   * we check if the URL contains "buy" before resetting the state to default.
   * this is so it doesnt overwrite the currency code in the buy field in the TradeCoinSelectionButton component.
   */
  useEffect(() => {
    if (location.search.includes("code")) return;
    if (currencyUserID.current !== customerId) {
      currencyUserID.current = customerId;
      resetToDefault();
    }
  }, [customerId, location]);

  /**
   * Handles the calculation of the trade summary by making a query to the backend.
   *
   * This function prepares a TradeSummaryDto object based on user inputs,
   * ensuring that properties with a value of 0 are removed to determine which
   * side (buy or sell) is known. It then attempts to post the trade summary data
   * to the server.
   */
  const handleTradeSummaryCalculation = async (): Promise<void> => {
    const tradeSummaryDto: TradeSummaryDto = {
      sell: { units: Number(sellInput.units), code: sellInput.code },
      buy: { units: Number(buyInput.units), code: buyInput.code },
    };

    if (knownSide === "sell") {
      tradeSummaryDto.buy.units = undefined;
    }

    if (knownSide === "buy") {
      tradeSummaryDto.sell.units = undefined;
    }
    // Remove properties with units === 0
    // this determines which side is known.
    if (tradeSummaryDto.sell.units === 0) tradeSummaryDto.sell.units = undefined;
    if (tradeSummaryDto.buy.units === 0) tradeSummaryDto.buy.units = undefined;

    // in some rare cases, if both inputs are 0, set the input element units that is focused to use value as a backup number.
    // usually occurs when the user switches currency and the input field is filled
    if (!tradeSummaryDto.sell.units && !tradeSummaryDto.buy.units) {
      if (knownSide === "sell") {
        // user inputted in sell field meaning sell field is the known side.
        tradeSummaryDto.sell.units = Number(sellInput.value);
      }
      if (knownSide === "buy") {
        // user inputted in buy field meaning buy field is the known side.
        tradeSummaryDto.buy.units = Number(buyInput.value);
      }
    }

    if (
      (!tradeSummaryDto.buy?.units || tradeSummaryDto.buy.units === 0) &&
      (!tradeSummaryDto.sell?.units || tradeSummaryDto.sell.units === 0)
    ) {
      return;
    }
    setDidFetch(true);

    postTradeSummary({ tradeSummaryDto })
      .unwrap()
      .then((summary): void => {
        // proactively block buying a token restricted for the user's country
        const restrictedMessage = getRestrictedCountryMessage(
          summary.buy.currencyConfig,
          address?.country,
        );
        if (restrictedMessage) {
          handleCustomError(restrictedMessage);
          return;
        }
        handleSummaryChange(summary);
        updateTradeSummary(summary);
        dispatch(setTradeRequest(tradeSummaryDto));
        // error message can come from a successfull response
        if (handleErrorFromSummaryResponse(summary)) return;
        // handle setting the summary / expected output values
      })
      .catch((error): void => {
        trackEvent("Error User View Swap Summary", { rejected: error.data.message }, "Swap");
        if (error.message.includes("minimum transaction")) {
          changeErrorMessage("belowMinTrade");
        } else {
          handleCustomError(`${error.message}. Press clear and try again`);
        }
      })
      .finally((): void => {
        setDidFetch(false);
      });
  };

  /**
   * State to track whether the summary has been fetched.
   * Avoid being able to 'calculate' the summary while calculating the summary is already is progress.
   */
  const [didFetch, setDidFetch] = useState<boolean>(false);

  /**
   * Determines if the trade summary can be fetched based on various conditions.
   * If you have HARD conditions that must be met before fetching the summary, add them here.
   * @returns {boolean} True if the summary can be fetched, otherwise false.
   */
  const canFetchSummary = useMemo((): boolean => {
    // if sell and buy input is 0, return false
    if (sellInput.value === "0" && buyInput.value === "0") return false;

    // if sell or buy input is "Select", return false
    if (sellInput.code === "Select" || buyInput.code === "Select") return false;

    // if the summary is being fetched, return false
    if (clearButton.isLoading) return false;

    // If there is an error, return false
    if (hasError) return false;

    // If the summary was JUST fetched for the first time.
    // avoids from summary being fetched again as all other conditions are not met.
    // return false
    if (didFetch) return false;

    return true;
  }, [
    sellInput.value,
    sellInput.code,
    buyInput.value,
    buyInput.code,
    clearButton.isLoading,
    hasError,
  ]);

  /**
   * Debounced function to handle trade summary calculation.
   * Delay is set to 500ms (0.5 seconds).
   */
  const debouncedCalculation = debounce(handleTradeSummaryCalculation, 500);

  /**
   * useEffect to automatically trigger the debounced calculation if the summary can be fetched.
   * Includes a fallback to ensure the calculation is triggered if debounce fails.
   */
  useEffect(() => {
    let fallbackTimeout: NodeJS.Timeout;

    // If the summary can be fetched, perform the debounced calculation

    if (canFetchSummary) {
      let isCalculating = true;

      const calculationPromise = debouncedCalculation();
      if (calculationPromise) {
        calculationPromise.then(() => {
          isCalculating = false;
          clearTimeout(fallbackTimeout);
        });
      }

      // Set a fallback to ensure the calculation is triggered if debounce fails
      fallbackTimeout = setTimeout(() => {
        if (isCalculating && !didFetch) {
          handleTradeSummaryCalculation();
        }
      }, 2500); // Fallback delay slightly longer than debounce delay
    }

    // Clean-up (cancel debounce and fallback) on unmount
    return (): void => {
      debouncedCalculation.cancel(); // Cancel debounce on unmount
      clearTimeout(fallbackTimeout); // Clear fallback timeout
    };
  }, [canFetchSummary, didFetch]);

  useEffect(() => {
    let amountAfterFee = 0;
    const feePercentage = tradeDetails.expectedOutput.fee ?? 3.5;

    // Calculate buy-side fiat value display
    if (sellInput.units !== "0" && sellInput.code !== "Select") {
      // Known side: sell. Use sell units and price, then apply fee percentage locally for immediate feedback
      const baseFiatValue = +sellInput.price * +sellInput.units;
      const feeValue = (feePercentage / 100) * baseFiatValue;
      amountAfterFee = baseFiatValue - feeValue;
    } else if (buyInput.units !== "0" && buyInput.code !== "Select") {
      // Known side: buy. Use buy units and price, then apply fee percentage locally for immediate feedback
      const baseFiatValue = +buyInput.units * +buyInput.price;
      const feeValue = (feePercentage / 100) * baseFiatValue;
      amountAfterFee = baseFiatValue - feeValue;
    }

    setBuyAmountValue(formatPrice(amountAfterFee, userBaseCurrency));
  }, [
    sellInput.price,
    sellInput.units,
    sellInput.code,
    buyInput.units,
    buyInput.price,
    buyInput.code,
    tradeDetails.expectedOutput.fee,
    userBaseCurrency,
  ]);

  useEffect(() => {
    const formattedInput = formatPrice(+sellInput.price * +sellInput.units, userBaseCurrency);
    setSellAmountValue(formattedInput);
  }, [sellInput.price, sellInput.units, userBaseCurrency]);

  const handleMaxTotal = (action: string): void => {
    if (action === "sell") {
      trackEvent("Button Click", { button: "Max button clicked" }, "Swap");
      dispatch(transerSellMaxTotal());
      dispatch(setKnownSide("sell"));
    }
  };

  /**
   * Handles the click event of the place order button by toggling the trade confirmation modal.
   */
  const handlePlaceOrderButton = (): void => {
    if (isGuest) {
      trackEvent("Guest Tried to place an order but was redirected to Verification", {}, "Swap");
      history.push("/application");
      return;
    }

    if (!handle2FACheck()) return;

    if (submitButton.text.includes("Deposit") && submitButton.text.includes("here")) {
      const currencyMatch = submitButton.text.match(/Deposit\s+(\w+)\s+here/);
      const currencyCode = currencyMatch ? currencyMatch[1] : "";

      trackEvent(
        "Button Click",
        { button: "Deposit redirect from error", currency: currencyCode },
        "Swap",
      );

      history.push(`/wallets`);
      return;
    }

    if (isGuest) {
      trackEvent("Guest Tried to place an order but was redirected to Verification", {}, "Swap");

      history.push("/application");
      return;
    }

    if (user?.isLocked || user?.isBypassLock) {
      trackEvent("User Locked", {}, "Swap");

      handleUserLockedChange({ isOpen: true });
      return;
    }
    // if sell or buy input is 0, return
    if (sellInput.value === "0" || buyInput.value === "0") return;

    // if sell or buy input is "Select", return
    if (sellInput.code === "Select" || buyInput.code === "Select") return;

    // if the summary is being fetched, return
    if (clearButton.isLoading) return;

    // If there is an error, return
    if (hasError) return;

    handleToggleTradeConfirmation();
  };

  /**
   * Generates a tooltip message for promotional fees based on partnership referral.
   */
  useEffect(() => {
    const getPromotionalFeeTooltip = async (): Promise<void> => {
      const feePercent: number = tradeDetails.expectedOutput.fee;
      if (!tradeDetails.partnershipCode) {
        handlePromotionalPlacardMessage(
          `A promotional fee of ${feePercent}% has been applied via your referral partner`,
        );
        return;
      }

      try {
        const resp = await getPartnershipInfo({
          referrer: tradeDetails.partnershipCode.toLowerCase(),
        }).unwrap();
        handlePromotionalPlacardMessage(
          `A promotional fee of ${feePercent}% has been applied via the referral partner ${resp.name}`,
        );
      } catch {
        handlePromotionalPlacardMessage(
          `A promotional fee of ${feePercent}% has been applied via your referral partner`,
        );
      }
    };

    getPromotionalFeeTooltip();
  }, [tradeDetails.partnershipCode]);

  if (isTradeConfirmationOpen) {
    return (
      <TradeConfirmationModal
        tradeType="swap"
        open={isTradeConfirmationOpen}
        sellUnits={sellInput.units}
        sellCode={sellInput.code}
        buyUnits={buyInput.units}
        buyCode={buyInput.code}
        priceData={formatPrice(buyInput.price, userBaseCurrency)}
        fee={tradeDetails.expectedOutput.fee}
        onClose={handleToggleTradeConfirmation}
      />
    );
  }

  return (
    <Wrapper data-testid="swap-card">
      <HeaderWrapper>
        <ClearButton
          isLoading={clearButton.isLoading}
          text={clearButton.text}
          onClick={() => {
            resetToDefault();
            trackEvent("Button Click", { button: "Clear button clicked" }, "Swap");
          }}
        />
      </HeaderWrapper>

      {/* Inputs */}
      <InputsWrapper>
        {/* Input 1 */}
        <InputWrapper sx={{ borderRadius: "16px 16px 0 0" }} error={hasError}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography>Selling</Typography>
            <BalanceDisplay
              code={sellInput.code === "Select" ? "" : sellInput.code}
              balance={sellInput.maxTotal.length > 1 ? sellInput.maxTotal : ""}>
              <MaxButton
                disabled={sellInput.code === "Select"}
                onClick={() => handleMaxTotal("sell")}>
                Max
              </MaxButton>
            </BalanceDisplay>
          </Box>
          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
            flexDirection="column"
            alignItems="center">
            <CustomInputField
              id="sellInput"
              autoComplete="off"
              inputRef={ref.sellInputRef}
              variant="standard"
              value={sellInput.units}
              onChange={handleSellInputChange}
              onKeyDown={handleSellInputKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              disabled={clearButton.isLoading}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
                disableUnderline: true,
                classes: {
                  input: classes.inputStyles,
                },
                inputProps: {
                  min: 0,
                  max: 1000000,
                  color: "#fff",
                },
                startAdornment: (
                  <TradeCoinSelectionButton
                    code={sellInput.code}
                    logoURL={sellInput.logoURL}
                    maxTotal={sellInput.maxTotal}
                    action="sell"
                    tradeType="swap"
                  />
                ),
              }}
            />
            <Box display="flex" alignSelf="flex-end" sx={{ color: "#5C6469" }}>
              {sellAmountValue}
            </Box>
          </Box>
        </InputWrapper>
        <OrderSwitch />
        {/* Input 2 */}
        <InputWrapper sx={{ borderRadius: "0 0 16px 16px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography>Buying</Typography>
            <BalanceDisplay
              code={buyInput.code === "Select" ? "" : buyInput.code}
              balance={buyInput.maxTotal.length > 1 ? buyInput.maxTotal : ""}
            />
          </Box>
          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
            flexDirection="column"
            alignItems="center">
            <CustomInputField
              id="buyInput"
              autoComplete="off"
              inputRef={ref.buyInputRef}
              variant="standard"
              value={buyInput.units}
              onChange={handleBuyInputChange}
              onKeyDown={handleBuyInputKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              disabled={clearButton.isLoading}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
                disableUnderline: true,
                classes: {
                  input: classes.inputStyles,
                },
                inputProps: {
                  min: 0,
                  max: 1000000,
                  color: "#fff",
                  textAlign: "end",
                },
                startAdornment: (
                  <TradeCoinSelectionButton
                    code={buyInput.code}
                    logoURL={buyInput.logoURL}
                    maxTotal={buyInput.maxTotal}
                    action="buy"
                    tradeType="swap"
                  />
                ),
              }}
            />
            <FeeText>{buyAmountValue} [Fee included]</FeeText>
          </Box>
        </InputWrapper>
      </InputsWrapper>

      {/* Error handling */}
      {error}
      <SummaryAndButtonWrapper>
        {tradeDetails.total !== 0 && (
          <TradeSummary
            title="Swap"
            amount={sellInput.units}
            currency={sellInput.code}
            lineItems={[
              {
                label: "Market Price",
                value: formatPrice(buyInput.price, userBaseCurrency),
                icon: "clear",
              },
            ]}
            fee={tradeDetails.expectedOutput.fee}
            feeTooltip={HELP_TOOLTIP}
            expectedOutput={{
              label: "Expected Output",
              value: `Approx. ${buyInput.units} ${buyInput.code}`,
            }}>
            {tradeDetails.isPromotional && (
              <PromotionalFeeDisplay message={promotionalPlacardMessage} />
            )}
          </TradeSummary>
        )}

        <TwoFAAlert />

        {isGuest && (
          <AlertCard
            titleText="Action Required"
            subText={`Please verify your account to access this feature. Verifying your account will allow you to deposit funds and make a trade. Once your account is verified, go to Settings → Security and toggle on "Enable 2 Factor Authentication" to gain full access to trading features.`}
            styleType={AlertStyleType.TRADE}
          />
        )}

        {/* Place order button */}
        <TradeSubmitButton
          text={submitButtonText}
          disabled={is2FAEnabled && isSubmitButtonDisabled}
          onClick={handlePlaceOrderButton}
          color={submitButton.color as "salmon" | "navy" | "neutral"}
          marginTop="40px"
        />
      </SummaryAndButtonWrapper>

      {/* Trade locked user modal */}
      {/* Prohibits user from progressing the order if their user is locked */}
      <TradeLockedUserModal
        open={userLocked.isOpen}
        onClose={() => handleUserLockedChange({ isOpen: false })}
      />
    </Wrapper>
  );
};
